'use client'

import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import * as stylex from '@stylexjs/stylex'
import { cloneElement, useId, useState } from 'react'
import type { AriaAttributes, ReactElement } from 'react'

import { colors, elevation, fonts, fontSizes, layers, lineHeights, radii, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  positioner: { zIndex: layers.tooltip },
  popup: {
    backgroundColor: colors.background,
    borderColor: colors.overlayBorder,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: elevation.tooltip,
    boxSizing: 'border-box',
    borderRadius: radii.small,
    color: colors.foreground,
    fontFamily: fonts.sans,
    fontSize: fontSizes.small,
    fontWeight: 400,
    lineHeight: lineHeights.small,
    maxWidth: 'calc(100vw - 32px)',
    overflowWrap: 'anywhere',
    paddingBlock: spacing.space8,
    paddingInline: spacing.space12,
  },
})

type TooltipTriggerDescription = Pick<AriaAttributes, 'aria-describedby'>

export type TooltipProps = {
  children: ReactElement<TooltipTriggerDescription>
  content: string
}

/**
 * Use for short supplementary text on an existing focusable control; choose Popover for content containing
 * controls or persistent interaction. Applications supply the text, and the trigger keeps its accessible name.
 * Existing aria-describedby references remain associated while the tooltip adds its description. Status:
 * supported.
 */
export function Tooltip({ children, content }: TooltipProps) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const describedBy = [children.props['aria-describedby'], open ? id : undefined].filter(Boolean).join(' ') || undefined

  return (
    <BaseTooltip.Root open={open} onOpenChange={setOpen}>
      <BaseTooltip.Trigger
        delay={500}
        closeDelay={100}
        render={cloneElement(children, { 'aria-describedby': describedBy })}
      />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={16}
          {...stylex.props(styles.positioner)}
        >
          <BaseTooltip.Popup id={id} role="tooltip" {...stylex.props(styles.popup)}>
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
